import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { fmtEUR, fmtNum } from "../lib/format";
import { Card } from "../components/ui/card";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  LineChart, Line, PieChart, Pie, Cell,
} from "recharts";
import {
  Wallet, TrendingUp, TrendingDown, PiggyBank, Building2, AlertTriangle, ArrowUpRight, ArrowDownRight,
} from "lucide-react";

const KPI = ({ label, value, icon: Icon, tone = "default", sub }) => {
  const toneCls = { default: "text-primary", pos: "text-emerald-600", neg: "text-destructive", warn: "text-amber-600" }[tone];
  return (
    <Card className="p-4" data-testid={`kpi-${label.toLowerCase().replace(/ /g, "-")}`}>
      <div className="flex items-start justify-between">
        <div className="text-xs text-muted-foreground uppercase tracking-wide">{label}</div>
        <Icon className={`w-4 h-4 ${toneCls}`} />
      </div>
      <div className={`kpi-num text-2xl font-bold mt-2 ${toneCls}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </Card>
  );
};

const CHART_COLORS = ["#1e3a5f", "#2b9bd4", "#16a34a", "#f59e0b", "#dc2626", "#7c3aed", "#0891b2"];

export default function Dashboard() {
  const [d, setD] = useState(null);
  const [horizon, setHorizon] = useState(30);
  useEffect(() => { api.get(`/dashboard?horizon_days=${horizon}`).then((r) => setD(r.data)).catch(() => {}); }, [horizon]);
  if (!d) return <div className="text-muted-foreground">Caricamento dashboard...</div>;
  const k = d.kpi;
  const HorizonSel = () => (
    <div className="flex gap-1" data-testid="horizon-selector">
      {[30, 90, 365].map((h) => (
        <button key={h} type="button" data-testid={`horizon-${h}`} onClick={() => setHorizon(h)}
          className={`px-2 py-0.5 rounded text-xs border ${horizon === h ? "bg-primary text-white border-primary" : "text-muted-foreground border-border hover:bg-muted"}`}>{h}gg</button>
      ))}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="dashboard">
      <div>
        <h1 className="text-3xl font-display font-bold text-primary">Dashboard Generale</h1>
        <p className="text-sm text-muted-foreground">Centro di controllo — dati in tempo reale dal database</p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Liquidità disponibile" value={fmtEUR(k.liquidita_disponibile)} icon={Wallet} tone={k.liquidita_disponibile < 0 ? "neg" : "pos"} sub="saldi + fido disponibile − impegni" />
        <KPI label="Saldo bancario" value={fmtEUR(k.saldo_bancario)} icon={PiggyBank} sub={<span>{k.fido_utilizzato > 0 && <>fido utilizzato {fmtEUR(k.fido_utilizzato)} · </>}{k.conti_da_banca > 0 ? `${k.conti_da_banca}/${k.conti_totali} conti da banca` : "da prima nota"}{Math.abs(k.scostamento_banca || 0) > 0.5 && <span className="text-amber-600"> · da riconciliare {fmtEUR(k.scostamento_banca)}</span>}</span>} />
        <KPI label="Cassa impegnata" value={fmtEUR(k.cassa_impegnata)} icon={TrendingDown} tone="warn" />
        <KPI label="Entrate previste" value={fmtEUR(k.entrate_prev_30)} icon={ArrowUpRight} tone="pos"
          sub={<span className="flex flex-wrap items-center justify-between gap-1"><span>di cui vendite unità {fmtEUR(k.vendite_prev || 0)}</span><HorizonSel /></span>} />
        <KPI label="Uscite previste" value={fmtEUR(k.uscite_prev_30)} icon={ArrowDownRight} tone="neg" sub={`prossimi ${horizon} giorni`} />
        <KPI label="Capitale da associati (attivo)" value={fmtEUR(k.capitale_raccolto)} icon={PiggyBank} />
        <KPI label="Capitale da rimborsare" value={fmtEUR(k.capitale_da_rimborsare)} icon={TrendingDown} tone="warn" />
        <KPI label="Quote utili da pagare" value={fmtEUR(k.rendimenti_da_pagare)} icon={TrendingDown} tone="warn" />
        <KPI label="Capitale proprio" value={fmtEUR(k.capitale_proprio)} icon={Wallet} />
        <KPI label="Operazioni attive" value={fmtNum(k.operazioni_attive)} icon={Building2} />
        <KPI label="In acquisizione" value={fmtNum(k.operazioni_acquisizione)} icon={Building2} />
        <KPI label="Valore operazioni" value={fmtEUR(k.valore_operazioni)} icon={Building2} />
        <KPI label="Ricavi previsti" value={fmtEUR(k.ricavi_previsti)} icon={TrendingUp} tone="pos" />
        <KPI label="Costi previsti" value={fmtEUR(k.costi_previsti)} icon={TrendingDown} />
        <KPI label="Margine previsto" value={fmtEUR(k.margine_previsto)} icon={TrendingUp} tone={k.margine_previsto >= 0 ? "pos" : "neg"} />
        <KPI label="Margine effettivo" value={fmtEUR(k.margine_effettivo)} icon={TrendingUp} tone={k.margine_effettivo >= 0 ? "pos" : "neg"} />
        <KPI label="Unità in vendita" value={fmtNum(k.unita_vendita)} icon={Building2} />
        <KPI label="Unità prenotate / preliminare" value={fmtNum(k.unita_prenotate)} icon={Building2} tone="warn" />
        <KPI label="Unità rogitate" value={fmtNum(k.unita_vendute)} icon={Building2} tone="pos" />
        <KPI label="Unità in lavorazione" value={fmtNum(k.unita_in_lavorazione)} icon={Building2} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="p-5 lg:col-span-2">
          <h3 className="font-display font-semibold mb-4">Cash Flow Mensile</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={d.cashflow}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" fontSize={12} /><YAxis fontSize={12} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <Tooltip formatter={(v) => fmtEUR(v)} /><Legend />
              <Bar dataKey="entrate" name="Entrate" fill="#16a34a" radius={[4, 4, 0, 0]} />
              <Bar dataKey="uscite" name="Uscite" fill="#dc2626" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-amber-500" /> Richiede Attenzione</h3>
          <div className="space-y-2 max-h-[280px] overflow-y-auto" data-testid="attention-panel">
            {d.attention.length === 0 && <div className="text-sm text-muted-foreground">Tutto regolare ✓</div>}
            {d.attention.map((a, i) => (
              <div key={i} className="flex items-start gap-2 text-sm p-2 rounded-lg bg-muted/50">
                <span className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${a.level === "rosso" ? "bg-destructive" : a.level === "giallo" ? "bg-amber-500" : "bg-emerald-500"}`} />
                <span>{a.link ? <Link to={a.link} className="hover:underline">{a.text}</Link> : a.text}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-5">
          <h3 className="font-display font-semibold mb-4">Margine per Operazione</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={d.capital_per_op} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis type="number" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="nome" fontSize={11} width={110} />
              <Tooltip formatter={(v) => fmtEUR(v)} /><Legend />
              <Bar dataKey="margine_previsto" name="Margine previsto" fill="#1e3a5f" radius={[0, 4, 4, 0]} />
              <Bar dataKey="margine_effettivo" name="Margine effettivo" fill="#2b9bd4" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <h3 className="font-display font-semibold mb-4">Entrate/Uscite per Categoria</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={d.by_category.map((c) => ({ ...c, abs: Math.abs(c.importo) }))} dataKey="abs" nameKey="categoria" cx="50%" cy="50%" outerRadius={100} label={(e) => e.categoria}>
                {d.by_category.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtEUR(v)} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
